import { lazy, Suspense, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import PreJoinScreens from './components/PreJoinScreens/PreJoinScreens';

import useHeight from './hooks/useHeight/useHeight';
import useRoomState from './hooks/useRoomState/useRoomState';
import { useAppState } from './state';
import useVideoContext from './hooks/useVideoContext/useVideoContext';
import { apiPostKnockAcknowledge, apiPostKnockClear } from './utils/api';
import { Box, CircularProgress } from '@mui/material';

const AfterJoinScreen = lazy(() => import('./components/AfterJoinScreen/AfterJoinScreen'));

const Container = styled('div')({
  display: 'grid',
  gridTemplateRows: '1fr auto',
});

export default function App() {
  const roomState = useRoomState();
  const { isKnocking, knockedRoomUpn } = useVideoContext();
  const { username } = useAppState();

  useEffect(() => {
    const clearKnock = async () => {
      if (!isKnocking) return;
      apiPostKnockAcknowledge(username, knockedRoomUpn, 1);
      apiPostKnockClear(username, knockedRoomUpn, '1');
    };

    window.addEventListener('beforeunload', clearKnock);
    return () => {
      window.removeEventListener('beforeunload', clearKnock);
    };
  }, [username, isKnocking, knockedRoomUpn]);

  // Here we would like the height of the main container to be the height of the viewport.
  // On some mobile browsers, 'height: 100vh' sets the height equal to that of the screen,
  // not the viewport. This looks bad when the mobile browsers location bar is open.
  // We will dynamically set the height with 'window.innerHeight', which means that this
  // will look good on mobile browsers even after the location bar opens or closes.
  const height = useHeight();

  return (
    <Container style={{ height }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {roomState === 'disconnected' ? (
          <PreJoinScreens />
        ) : (
          <Suspense fallback={<Loading />}>
            <AfterJoinScreen />
          </Suspense>
        )}
      </LocalizationProvider>
    </Container>
  );
}

const Loading = () => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress />
  </Box>
);
